import moment from 'moment';
import {
  newCategory,
  newImage,
  newMiniTool,
  newParagraph,
  newPdf,
  newSubTopic,
  newTopic,
  newVideo,
} from '../components/POCCMSPortal/POCToolCreator/helper/contentObjectCreator';
import {convertToRaw} from 'draft-js';
import {stateFromHTML} from 'draft-js-import-html';

export const convertingToFinalObject = displayPocToolContent => {
  // The convertingToFinalObject method transforms structured content represented in the
  // displayPocToolContent array into a hierarchical final object.
  // It tracks the current context (minitool, category, topic, subtopic) while processing each item and organizes them accordingly.
  // The resulting finalObject represents the content in a structured format suitable for backend communication.

  const finalArray = [];

  let currentMinitool = null;
  let currentCategory = null;
  let currentTopic = null;
  let currentSubTopic = null;

  displayPocToolContent.forEach(item => {
    const {type, value, id} = item;

    if (type === 'minitool') {
      currentMinitool = {
        name: value,
        order: finalArray.length,
        categories: [],
      };
      currentCategory = null;
      currentTopic = null;
      currentSubTopic = null;
      finalArray.push(currentMinitool);
    } else if (type === 'category') {
      if (!currentMinitool) {
        // If there is no current minitool, add the category directly to finalObject.
        currentCategory = {
          name: value,
          order: finalArray.length,
          topics: [],
          content: [],
        };
        currentTopic = null;
        currentSubTopic = null;
        finalArray.push(currentCategory);
      } else {
        currentCategory = {
          name: value,
          order: currentMinitool.categories.length || 0,
          topics: [],
          content: [],
        };
        currentTopic = null;
        currentSubTopic = null;
        currentMinitool.categories.push(currentCategory);
      }
    } else if (type === 'topic') {
      currentTopic = {
        name: value,
        order:
          currentCategory?.topics.length + currentCategory?.content.length || 0,
        subTopics: [],
        content: [],
      };
      currentSubTopic = null;
      currentCategory?.topics.push(currentTopic);
    } else if (type === 'subTopic') {
      currentSubTopic = {
        name: value,
        order:
          currentTopic?.subTopics.length + currentTopic?.content.length || 0,
        content: [],
        description: '',
      };
      currentTopic?.subTopics.push(currentSubTopic);
    } else if (['paragraph', 'img', 'video', 'pdf'].includes(type)) {
      // The currentContent object represents a content item and has properties like contentURL,
      // contentText, id, type, and order. The order property calculates the position of the current
      // content item within its context (subtopic, topic, category, or minitool) based on the number of
      //  similar content items of the same type. This helps maintain the order of content items when processing
      // or displaying them.
      if (item?.attachTo === 'lastSubtopic') {
        const currentContent = {
          contentURL: value,
          contentText: '',
          id: id,
          type: type,
          order:
            (currentTopic.subTopics?.length ?? 0) +
            (currentTopic.content?.length ?? 0),
        };
        currentTopic.content.push(currentContent);
      } else if (item?.attachTo === 'lastTopic') {
        const currentContent = {
          contentURL: value,
          contentText: '',
          id: id,
          type: type,
          order:
            (currentCategory.topics?.length ?? 0) +
            (currentCategory.content?.length ?? 0),
        };
        currentCategory.content.push(currentContent);
      } else {
        const currentContent = {
          contentURL: value,
          contentText: '',
          id: id,
          type: type,
          order: currentSubTopic
            ? currentSubTopic.content.length
            : currentTopic
            ? (currentTopic.subTopics?.length ?? 0) +
              (currentTopic.content?.length ?? 0)
            : currentCategory
            ? (currentCategory.topics?.length ?? 0) +
              (currentCategory.content?.length ?? 0)
            : 0,
        };

        if (currentSubTopic) {
          currentSubTopic.content.push(currentContent);
        } else if (currentTopic) {
          currentTopic.content.push(currentContent);
        } else if (currentCategory) {
          currentCategory.content.push(currentContent);
        }
      }
    }
  });
  return finalArray;
};

export const convertingPOCToolToDisplayPocToolContent = finalArray => {
  const displayPocToolContent = [];

  const processContent = (content, index, attachedTo?) => {
    let paragraphItem;
    let imgItem;
    let videoItem;
    let pdfItem;
    let contentState;
    switch (content.type) {
      case 'paragraph':
        paragraphItem = newParagraph(index);
        paragraphItem.value = content.contentURL;

        contentState = stateFromHTML(content.contentURL, {
          customInlineFn: (tagName, InlineCreator) => {
            if (tagName.tagName === 'SUP') {
              return InlineCreator.Style('SUPERSCRIPT');
            } else if (tagName.tagName === 'SUB') {
              return InlineCreator.Style('SUBSCRIPT');
            }
          },
        });

        paragraphItem.rawContentState = convertToRaw(contentState);

        paragraphItem.id = content.id;
        if (attachedTo) {
          paragraphItem.attachTo = attachedTo;
        }
        displayPocToolContent.push(paragraphItem);
        break;
      case 'img':
        imgItem = newImage(index);
        imgItem.value = content.contentURL;
        imgItem.id = content.id;
        if (attachedTo) {
          imgItem.attachTo = attachedTo;
        }
        displayPocToolContent.push(imgItem);
        break;
      case 'video':
        videoItem = newVideo(index);
        videoItem.value = content.contentURL;
        videoItem.id = content.id;
        if (attachedTo) {
          videoItem.attachTo = attachedTo;
        }
        displayPocToolContent.push(videoItem);
        break;
      case 'pdf':
        pdfItem = newPdf(index);
        pdfItem.value = content.contentURL;
        pdfItem.id = content.id;
        if (attachedTo) {
          pdfItem.attachTo = attachedTo;
        }
        displayPocToolContent.push(pdfItem);
        break;
      default:
        break;
    }
  };

  const processSubTopics = subtopic => {
    const subtopicItem = newSubTopic();
    subtopicItem.value = subtopic.name;
    subtopicItem.id = subtopic.id;
    displayPocToolContent.push(subtopicItem);
    subtopic.content
      .slice()
      .sort((a, b) => a.order - b.order)
      .forEach((content, index) => processContent(content, index));
  };

  const processTopic = topic => {
    const topicItem = newTopic();
    topicItem.value = topic.name;
    topicItem.id = topic.id;
    displayPocToolContent.push(topicItem);
    let isAttachedToTopics = false;

    [...topic.content, ...topic.subTopics]
      .sort((a, b) => a.order - b.order)
      .forEach((item, index) => {
        if ('contentURL' in item) {
          // Process category.content logic here
          processContent(item, index, isAttachedToTopics && 'lastSubtopic');
        } else {
          isAttachedToTopics = true;
          // Process category.topics logic here
          processSubTopics(item);
        }
      });
  };

  finalArray
    .slice()
    .sort((a, b) => a.order - b.order)
    .forEach(category => {
      const categoryItem = newCategory();
      categoryItem.value = category.name;
      categoryItem.id = category.id;
      displayPocToolContent.push(categoryItem);
      let isAttachedToTopics = false;
      [...category.content, ...category.topics]
        .sort((a, b) => a.order - b.order)
        .forEach((item, index) => {
          if ('contentURL' in item) {
            processContent(item, index, isAttachedToTopics && 'lastTopic');
          } else {
            isAttachedToTopics = true;
            processTopic(item);
          }
        });
    });
  return displayPocToolContent;
};

export const convertingMiniToolsToDisplayPocToolContent =
  minitoolsWithConent => {
    const displayPocToolContent = [];
    minitoolsWithConent
      .sort((a, b) => a.order - b.order)
      .forEach(minitool => {
        const minitoolItem = newMiniTool();
        minitoolItem.value = minitool.name;
        minitoolItem.id = minitool.id;
        displayPocToolContent.push(minitoolItem);

        const MiniToolcontent = convertingPOCToolToDisplayPocToolContent(
          minitool.categories,
        );
        displayPocToolContent.push(...MiniToolcontent);
      });
    return displayPocToolContent;
  };

export const formatDate = inputDateString => {
  const date = moment(inputDateString);
  const formattedDate = date.format('MM/DD/YYYY');
  return formattedDate;
};
